<template>
  <div class="app-container">
      <header-bar></header-bar>
      <div class="content-wrapper">
        <div class="empty-left"></div>
        <div class="left-sidenotes"></div>  
        <div class ="main">
            <p class="header">Welcome to Ansable VT.</p>
            <p class="bodytext" style="font-style: italic;">[In a hurry? Scroll down and enter your information to complete your sign-in.]</p>
            <p class="bodytext">In Vermont's small towns, staying informed about local government and community affairs should be simple – but it rarely is. Even in communities of just a few hundred people, participation proves challenging. Weekday meetings stretch for hours, grainy audio and minutes can be hard to parse, and despite excellent regional publications, many small towns are news deserts. Too often, critical information travels through word of mouth rather than reliable channels.</p>
            <p class="bodytext">Ansable transforms how Vermonters engage with their local institutions and access community news. We aggregate and promote Vermont's trusted news sources – including the Addison Independent, VT Digger, and others – bringing their coverage into one streamlined feed while respecting their paywalls and directing readers to their platforms. In conjunction, we address the rural reporting gap by providing AI-assisted, editor-verified summaries of local government meetings, making the content of lengthy selectboard, school board, and town meetings accessible to busy residents.</p>
            <p class="bodytext">This content is combined with a personalized, location-based approach to civic engagement. When residents log in, they see updates relevant to their specific town, including recent meeting summaries, newly posted minutes, and ongoing local discussions. Our searchable database allows citizens to track specific issues over time, from road maintenance projects to school board decisions. Community members can contribute their insights alongside meeting summaries, fostering more informed discussions and a fuller understanding of local issues.</p>
            <p class="bodytext">Small towns run on citizen engagement, but staying informed shouldn't be a burden. Ansable distills Vermont's local news and government decisions into clear, accessible updates that keep you connected to what matters in your community.</p>
            <p class="subhead">Enter your information.</p>
            <p class="bodytext" style="margin-bottom: 20px;">Our service is based on your home address (similar to Front Porch Forums). By providing your information, you accept our <router-link to="/policy" target="_blank" class="available-link"> Terms of Service and Privacy Policy</router-link>. </p>
            <form @submit.prevent="handleSubmit" class="form-container">
                <StyledInput
                    id="displayName"
                    label="Name"
                    v-model="localUserInfo.displayName"
                    required
                />
                <StyledInput
                    id="addressLine1"
                    label="Address Line 1"
                    v-model="localUserInfo.address.line1"
                    required
                />
                <StyledInput
                    id="addressLine2"
                    label="Address Line 2"
                    v-model="localUserInfo.address.line2"
                />
                <div class="city-state-zip">
                    <StyledInput
                    id="city"
                    label="City"
                    v-model="localUserInfo.address.city"
                    required
                    />
                    <StyledInput
                    id="state"
                    label="State"
                    v-model="localUserInfo.address.state"
                    required
                    />
                    <StyledInput
                    id="zip"
                    label="ZIP Code"
                    v-model="localUserInfo.address.zip"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    required
                    />
                </div>

                <div class="actions">
                    <button type="submit" class="save-button" :disabled="saving">
                    {{ saving ? 'Saving...' : 'Accept and Save' }}
                    </button>
                </div>
            </form>
        </div>
        <div class="right-sidenotes"></div>
      </div>
  </div>  
</template>

<script>
import StyledInput from "./Settings Components/StyledInput.vue";
import HeaderBar from "./WelcomeHeader.vue";
import { updateDoc, doc, getDoc } from "firebase/firestore";
import { auth, db } from "@/firebase";
import { useRouter } from "vue-router";

export default {
  name: "WelcomePage",
  components: {
    StyledInput,
    HeaderBar,
  },
  data() {
    return {
      localUserInfo: {
        displayName: "",
        address: {
          line1: null,
          line2: null,
          city: null,
          state: "VT",
          zip: null,
        },
        enabledPublications: [],
        enabledTownSettings: ['selectBoard', 'schoolBoard', 'otherTownMeetings', 'commissionGroup'],
      },
      saving: false,
    };
  },
  setup() {
    const router = useRouter();
    return { router };
  },
  methods: {
    /* async handleSubmit() {
      if (!auth.currentUser) {
        alert("No authenticated user. Please log in.");
        return;
      }

      if (
            !this.localUserInfo.displayName ||
            !this.localUserInfo.address.line1 ||
            !this.localUserInfo.address.city ||
            !this.localUserInfo.address.state ||
            !this.localUserInfo.address.zip
        ) {
            alert("Please fill out all required fields.");
            return;
        }

      this.saving = true;
      try {
        const updateData = {
          displayName: this.localUserInfo.displayName,
          address: this.localUserInfo.address,
          lastUpdated: new Date(),
          enabledPublications: this.localUserInfo.enabledPublications,
          enabledTownSettings: this.localUserInfo.enabledTownSettings,
        };

        await updateDoc(doc(db, "users", auth.currentUser.uid), updateData);
        alert("Information saved successfully!");
        this.router.push("/home");
      } catch (err) {
        console.error("Error saving user info:", err);
        alert("Couldn’t save your info. Try again.");
      } finally {
        this.saving = false;
      }
    }, */
    async handleSubmit() {
      if (!auth.currentUser) {
        alert("No authenticated user. Please log in.");
        return;
      }
      
      const { address } = this.localUserInfo;
      if (!address.line1 || !address.city || !address.state || !address.zip) {
        alert("Please fill out all required fields.");
        return;
      }
      
      // Validate town (case-insensitive)
      /* const allowedTowns = ["Brandon", "Goshen"];
      const inputTown = address.city.trim();
      if (!allowedTowns.some(town => town.toLowerCase() === inputTown.toLowerCase())) {
        alert("Invalid town. Only Brandon and Goshen are allowed.");
        return;
      } */

      // Validate town (case-insensitive) and normalize it
      const allowedTowns = ["Brandon", "Goshen"];
      const inputTown = address.city.trim();
      const normalizedTown = inputTown.charAt(0).toUpperCase() + inputTown.slice(1).toLowerCase();

      if (!allowedTowns.includes(normalizedTown)) {
        alert("Invalid town. Only Brandon and Goshen are allowed.");
        return;
      }
      
      this.saving = true;
      try {
        // Get county from townMapping
        const townDocRef = doc(db, "townMapping", normalizedTown);
        const townDocSnap = await getDoc(townDocRef);
        if (!townDocSnap.exists()) {
          alert("Invalid town. Only Brandon and Goshen are allowed.");
          this.saving = false;
          return;
        }
        const { county } = townDocSnap.data();
        if (!county) {
          alert("County data missing for this town.");
          this.saving = false;
          return;
        }
        
        // Get default publications from countyMapping
        const countyDocRef = doc(db, "countyMapping", county);
        const countyDocSnap = await getDoc(countyDocRef);
        if (!countyDocSnap.exists()) {
          alert("County mapping not found.");
          this.saving = false;
          return;
        }
        const countyData = countyDocSnap.data();
        if (!countyData.Publications || !Array.isArray(countyData.Publications)) {
          alert("Invalid publications data for county.");
          this.saving = false;
          return;
        }
        
        // Set the default enabledPublications based on county mapping
        this.localUserInfo.enabledPublications = countyData.Publications;

        //update user input to ensure proper filtering
        this.localUserInfo.address.city = normalizedTown;
        
        const updateData = {
          address: this.localUserInfo.address,
          lastUpdated: new Date(),
          enabledPublications: this.localUserInfo.enabledPublications,
          enabledTownSettings: this.localUserInfo.enabledTownSettings,
        };
        
        await updateDoc(doc(db, "users", auth.currentUser.uid), updateData);
        alert("Information saved successfully!");
        this.router.push("/home");
      } catch (err) {
        console.error("Error saving user info:", err);
        alert("Couldn’t save your info. Try again.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>


<style> 
* {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
  }

html, body {
  height: 100%;
}

.app-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.content-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  overflow: hidden;
  background-color: #fffff8;
}

.empty-left{
  display: flex;
  flex: 1;
}

.content-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  overflow: hidden;
}

.empty-left{
  display: flex;
  flex: 1;
  background-color: #fffff8;
}

.right-sidenotes, .left-sidenotes {
  display: flex;
  flex: 1;
  background-color: #fffff8;
}

.main {
  flex: 3;
  background-color: #fffff8;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: auto;
  max-width: 1200px;
}

.header {
  text-align: left;
  padding-top: 20px;
  padding-bottom: 20px;
  font-size: 2rem;
  font-weight: 600;
  font-family: inherit;
  color: #545051;
}


.right-sidenotes, .left-sidenotes {
  display: flex;
  flex: 1;
  background-color: #fffff8;
}

.divider {
  display: block;
  width: 100%;
  height: 1px;
  background-color: #545051;
  min-height: 1px; 
  margin: 10px 0;
}

.subhead {
  text-align: left;
  font-size: 1.5rem;
  font-weight: 600;
  font-family: inherit;
  color: #545051;
  line-height: 1.6rem;
  margin-top:30px;
  padding-bottom: 10px;
}

.bodytext {
    margin-bottom: .75rem;
    text-align: left;
    font-size: 1rem;
    font-family: inherit;
}

.available-link {
  color: #545051;
  cursor: pointer;
  text-decoration: underline dashed;
  text-underline-offset: 2px;
  text-decoration-skip-ink: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .empty-left,
  .right-sidenotes,
  .left-sidenotes {
    display: none;
  }

  .main {
    flex: 1;
    max-width: 100%;
    padding: 0 1rem; /* Optional: add padding for better readability */
  }

  .viz-area {
    overflow: auto;
  }
}

@media (min-width: 768px) {
  .empty-left, .left-sidenotes {
    display: none;
  }

  .right-sidenotes {
    display: none;
  }

  .main {
    flex: 2;
    max-width: 800px;
    padding: 0 2rem;
    margin: 0 auto;
  }
}

@media (min-width: 1024px) {
  .empty-left, .right-sidenotes, .left-sidenotes {
    display: flex;
  }

  .main {
    flex: 3;
    max-width: 1200px;
    padding: 0;
  }
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.city-state-zip {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 24px;
  margin-top: 8px;
}

.actions {
  margin-top: 32px;
  display: flex;
  justify-content: flex-start;
}

.save-button {
  background-color: #545051;
  color: white;
  border: none;
  padding: 8px 24px;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 400;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: 20px;
}

.save-button:hover {
  background-color: #636063;
}

.save-button:disabled {
  background-color: #a8a8a8;
  cursor: not-allowed;
}

</style>